import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  PhotographIcon,
  VideoCameraIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon } from '@heroicons/react/solid';

import { TEXT_MODELS } from '../workflows/nodes/TextGenerationNode';
import { EDIT_MODELS } from '../workflows/nodes/ImageEditNode';

type NodeType = 'input' | 'process' | 'output';

type Node = {
  id: string;
  label: string;
  subLabel?: string;
  icon: any;
  x: number; // percentage
  y: number; // percentage
  mobileX?: number; // percentage for mobile
  mobileY?: number; // percentage for mobile
  type: NodeType;
  color: string;
  inputs: string[]; // IDs of input nodes
  activeDelay: number; // When to activate in the loop
  duration: number; // How long to stay active
};

const nodes: Node[] = [
  {
    id: 'source',
    label: 'Text Generation',
    subLabel: TEXT_MODELS[0].label,
    icon: DocumentTextIcon,
    x: 15,
    y: 40, // Top-left
    mobileX: 50,
    mobileY: 10,
    type: 'input',
    color: '#3b82f6', // blue
    inputs: [],
    activeDelay: 0,
    duration: 2,
  },
  {
    id: 'image-gen',
    label: 'Text to Image',
    subLabel: 'Nano Banana',
    icon: PhotographIcon,
    x: 50,
    y: 60, // Lower-middle
    mobileX: 75,
    mobileY: 32,
    type: 'process',
    color: '#8b5cf6', // purple
    inputs: ['source'],
    activeDelay: 2,
    duration: 2.5,
  },
  {
    id: 'image-gen-mj',
    label: 'Text to Image',
    subLabel: 'Midjourney',
    icon: PhotographIcon,
    x: 50,
    y: 20, // Top-middle
    mobileX: 28,
    mobileY: 32,
    type: 'process',
    color: '#8b5cf6', // purple
    inputs: ['source'],
    activeDelay: 2,
    duration: 2.5,
  },
  {
    id: 'video-gen',
    label: 'Image to Video',
    subLabel: 'Veo 4',
    icon: VideoCameraIcon,
    x: 85,
    y: 20, // Top-right
    mobileX: 28,
    mobileY: 60,
    type: 'output',
    color: '#f97316', // orange
    inputs: ['image-gen-mj'],
    activeDelay: 4.5,
    duration: 2.5,
  },
  {
    id: 'image-to-image',
    label: 'Image to Image',
    subLabel: EDIT_MODELS[0].label,
    icon: PhotographIcon,
    x: 85,
    y: 80, // Bottom-right
    mobileX: 50,
    mobileY: 85,
    type: 'output',
    color: '#10b981', // emerald
    inputs: ['image-gen'],
    activeDelay: 4.5,
    duration: 2.5,
  },
  {
    id: 'image-upscale',
    label: 'Upscale Image',
    subLabel: 'Creative 4x',
    icon: SparklesIcon,
    x: 85,
    y: 60, // Middle-right
    mobileX: 75,
    mobileY: 60,
    type: 'output',
    color: '#059669', // emerald-600 like
    inputs: ['image-gen'],
    activeDelay: 4.5,
    duration: 2.5,
  },
];

const NODE_WIDTH_MD = 192; // w-48
const NODE_WIDTH_SM = 144; // w-36

const Cursor = ({
  color,
  label,
  x,
  y,
}: {
  color: string;
  label: string;
  x: number;
  y: number;
}) => (
  <motion.div
    className='absolute pointer-events-none z-50'
    animate={{ left: `${x}%`, top: `${y}%` }}
    transition={{
      duration: 2,
      ease: 'easeInOut',
    }}
  >
    <svg
      className='w-5 h-5 drop-shadow-md'
      viewBox='0 0 24 24'
      fill={color}
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M3 3L10.07 19.97L12.58 12.58L19.97 10.07L3 3Z'
        stroke='white'
        strokeWidth='2'
        strokeLinejoin='round'
      />
    </svg>
    <div
      className='absolute left-4 top-4 px-2 py-0.5 rounded-full text-[10px] font-medium text-white shadow-lg whitespace-nowrap'
      style={{ backgroundColor: color }}
    >
      {label}
    </div>
  </motion.div>
);

const WorkflowVisualization = ({
  forceDesktop = false,
}: {
  forceDesktop?: boolean;
}) => {
  const [time, setTime] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const LOOP_DURATION = 8; // Total seconds for one cycle

  // Handle resizing
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    let startTime = Date.now();
    let animationFrameId: number;

    const animate = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;
      setTime(elapsed % LOOP_DURATION);
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const isMobile = forceDesktop
    ? false
    : dimensions.width > 0 && dimensions.width < 768;
  const NODE_HEIGHT = 100; // Approximate node height including ports

  const getNodePos = (node: Node) => {
    if (isMobile && node.mobileX !== undefined && node.mobileY !== undefined) {
      return { x: node.mobileX, y: node.mobileY };
    }
    return { x: node.x, y: node.y };
  };

  return (
    <div
      ref={containerRef}
      className='w-full h-full min-h-[700px] md:min-h-[500px] relative overflow-hidden rounded-3xl bg-[#0F1117] border border-white/5 shadow-2xl select-none group'
    >
      {/* Background Pattern */}
      <div
        className='absolute inset-0 opacity-20 pointer-events-none'
        style={{
          backgroundImage: 'radial-gradient(#4f46e5 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          backgroundPosition: '-15px -15px',
        }}
      ></div>

      {/* Ambient Glows - hidden on mobile for performance */}
      <div className='hidden md:block absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[100px] pointer-events-none' />
      <div className='hidden md:block absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] pointer-events-none' />

      {/* SVG Layer for Connections */}
      <svg className='absolute inset-0 w-full h-full pointer-events-none overflow-visible'>
        <defs>
          <linearGradient id='flow-gradient' x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor='#3b82f6' stopOpacity='0' />
            <stop offset='50%' stopColor='#8b5cf6' stopOpacity='1' />
            <stop offset='100%' stopColor='#ec4899' stopOpacity='0' />
          </linearGradient>
          <linearGradient
            id='flow-gradient-purple'
            x1='0%'
            y1='0%'
            x2='100%'
            y2='0%'
          >
            <stop offset='0%' stopColor='#8b5cf6' stopOpacity='0' />
            <stop offset='50%' stopColor='#8b5cf6' stopOpacity='1' />
            <stop offset='100%' stopColor='#8b5cf6' stopOpacity='0' />
          </linearGradient>
          <filter
            id='glow-path'
            filterUnits='userSpaceOnUse'
            x='-50%'
            y='-50%'
            width='200%'
            height='200%'
          >
            <feGaussianBlur stdDeviation='2' result='blur' />
            <feComposite in='SourceGraphic' in2='blur' operator='over' />
          </filter>
        </defs>

        {dimensions.width > 0 &&
          nodes.map((node) =>
            node.inputs.map((inputId) => {
              const startNode = nodes.find((n) => n.id === inputId)!;
              const isActive =
                time >= startNode.activeDelay &&
                time <= node.activeDelay + node.duration;

              const nodeWidth =
                dimensions.width < 768 ? NODE_WIDTH_SM : NODE_WIDTH_MD;

              const startPos = getNodePos(startNode);
              const endPos = getNodePos(node);

              let x1, y1, x2, y2, pathD;

              if (isMobile) {
                // Vertical Flow: Bottom to Top
                // Start: Bottom of startNode
                x1 = (startPos.x / 100) * dimensions.width;
                y1 = (startPos.y / 100) * dimensions.height + NODE_HEIGHT / 2;

                // End: Top of endNode
                x2 = (endPos.x / 100) * dimensions.width;
                y2 = (endPos.y / 100) * dimensions.height - NODE_HEIGHT / 2;

                const distY = Math.abs(y2 - y1);
                // Vertical Bezier
                pathD = `M ${x1} ${y1} C ${x1} ${y1 + distY * 0.5}, ${x2} ${y2 - distY * 0.5}, ${x2} ${y2}`;
              } else {
                // Horizontal Flow: Right to Left
                x1 = (startPos.x / 100) * dimensions.width + nodeWidth / 2;
                y1 = (startPos.y / 100) * dimensions.height;

                x2 = (endPos.x / 100) * dimensions.width - nodeWidth / 2;
                y2 = (endPos.y / 100) * dimensions.height;

                const dist = Math.abs(x2 - x1);
                const yOffset = y1 === y2 ? 0.1 : 0;
                pathD = `M ${x1} ${y1} C ${x1 + dist * 0.5} ${y1 + yOffset}, ${
                  x2 - dist * 0.5
                } ${y2 - yOffset}, ${x2} ${y2}`;
              }

              const isPurpleSource = startNode.color === '#8b5cf6';

              return (
                <g key={`${inputId}-${node.id}`}>
                  {/* Background Wire */}
                  <path
                    d={pathD}
                    fill='none'
                    stroke='#1e293b'
                    strokeWidth='2'
                    vectorEffect='non-scaling-stroke'
                  />

                  {/* Active Flow Animation */}
                  {isActive && (
                    <motion.path
                      d={pathD}
                      fill='none'
                      stroke={
                        isPurpleSource
                          ? 'url(#flow-gradient-purple)'
                          : 'url(#flow-gradient)'
                      }
                      strokeWidth='3'
                      vectorEffect='non-scaling-stroke'
                      strokeDasharray='10 10'
                      initial={{ strokeDashoffset: 20 }}
                      animate={{ strokeDashoffset: 0 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                      filter='url(#glow-path)'
                    />
                  )}

                  {/* Traveling Packet */}
                  {isActive && (
                    <motion.circle r='4' fill='#fff' filter='url(#glow-path)'>
                      <animateMotion
                        dur='1.5s'
                        repeatCount='indefinite'
                        path={pathD}
                        calcMode='spline'
                        keySplines='0.4 0 0.2 1'
                        keyTimes='0;1'
                      />
                    </motion.circle>
                  )}
                </g>
              );
            }),
          )}
      </svg>

      {/* Nodes Layer */}
      <div className='absolute inset-0 pointer-events-none'>
        {nodes.map((node) => {
          // Node is "processing" when time is within its active window
          const isProcessing =
            time >= node.activeDelay && time < node.activeDelay + node.duration;
          const isDone = time >= node.activeDelay + node.duration;

          const pos = getNodePos(node);

          return (
            <div
              key={node.id}
              className='absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto'
              style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
            >
              <NodeComponent
                node={node}
                isProcessing={isProcessing}
                isDone={isDone}
                orientation={isMobile ? 'vertical' : 'horizontal'}
              />
            </div>
          );
        })}
      </div>

      <div className='absolute bottom-4 left-4 flex items-center gap-2 pointer-events-none'>
        <div className='px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-xs font-mono text-white/40'>
          {time.toFixed(2)}s / {LOOP_DURATION}s
        </div>
      </div>

      {/* Collaborator Cursors */}
      {!isMobile && (
        <>
          <Cursor
            color='#ec4899' // Pink
            label='Melissa'
            x={time < 2 ? 15 : time < 4.5 ? 50 : 85}
            y={time < 2 ? 40 : time < 4.5 ? 60 : 70}
          />
          <Cursor
            color='#06b6d4' // Cyan
            label='Mark'
            x={time < 2.5 ? 15 : time < 5 ? 50 : 85}
            y={time < 2.5 ? 40 : time < 5 ? 20 : 20}
          />
        </>
      )}
    </div>
  );
};

const NodeComponent = ({
  node,
  isProcessing,
  isDone,
  orientation = 'horizontal',
}: {
  node: Node;
  isProcessing: boolean;
  isDone: boolean;
  orientation?: 'horizontal' | 'vertical';
}) => {
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className='relative group cursor-pointer'
    >
      {/* Selection Glow */}
      <motion.div
        className='absolute -inset-0.5 rounded-xl blur opacity-0 transition-opacity duration-300'
        animate={{ opacity: isProcessing ? 0.6 : 0 }}
        style={{ backgroundColor: node.color }}
      />

      {/* Node Body */}
      <div
        className={`relative w-36 md:w-48 rounded-xl border backdrop-blur-xl overflow-hidden transition-all duration-300 
        ${
          isProcessing
            ? 'bg-slate-900/90 border-white/20 ring-1 ring-white/20'
            : isDone
              ? 'bg-slate-900/80 border-emerald-500/50 ring-1 ring-emerald-500/20'
              : 'bg-slate-900/80 border-white/5 hover:border-white/10'
        }`}
      >
        {/* Header */}
        <div className='px-3 py-2 border-b border-white/5 bg-white/5 flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            {(() => {
              const NodeIcon = node.icon;
              return (
                <NodeIcon
                  className={`w-4 h-4 ${isProcessing ? 'text-white' : 'text-gray-500'}`}
                  style={{ color: isProcessing ? node.color : undefined }}
                />
              );
            })()}
            <span
              className={`text-xs font-semibold tracking-wide ${isProcessing ? 'text-white' : 'text-gray-400'}`}
            >
              {node.label}
            </span>
          </div>
          {/* Status Indicator */}
          {isDone ? (
            <CheckCircleIcon className='w-4 h-4 text-emerald-500' />
          ) : (
            <div
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-300
                  ${isProcessing ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.6)]' : 'bg-gray-600'}`}
            />
          )}
        </div>

        {/* Content */}
        <div className='p-3'>
          <div className='flex justify-between items-center'>
            {orientation === 'horizontal' && (
              <span className='text-[10px] text-gray-500 font-mono uppercase tracking-wider'>
                {node.type}
              </span>
            )}
            {node.subLabel && (
              <span className='text-[10px] text-gray-400 bg-white/5 px-1.5 py-0.5 rounded'>
                {node.subLabel}
              </span>
            )}
          </div>

          {/* Progress Bar (Fake) */}
          <div className='h-1 w-full bg-white/5 rounded-full mt-3 overflow-hidden'>
            <motion.div
              className='h-full bg-gradient-to-r from-blue-500 to-violet-500'
              initial={{ width: '0%' }}
              animate={{
                width: isProcessing ? '100%' : isDone ? '100%' : '0%',
              }}
              transition={{
                duration: isProcessing ? node.duration : 0,
                ease: 'linear',
              }}
            />
          </div>
        </div>
      </div>

      {/* Input/Output Ports */}
      {orientation === 'horizontal' ? (
        <>
          <div className='absolute top-1/2 -left-1.5 w-3 h-3 -mt-1.5 rounded-full bg-[#1e293b] border border-gray-600 z-10' />
          <div className='absolute top-1/2 -right-1.5 w-3 h-3 -mt-1.5 rounded-full bg-[#1e293b] border border-gray-600 z-10' />
        </>
      ) : (
        <>
          <div className='absolute left-1/2 -top-1.5 w-3 h-3 -ml-1.5 rounded-full bg-[#1e293b] border border-gray-600 z-10' />
          <div className='absolute left-1/2 -bottom-1.5 w-3 h-3 -ml-1.5 rounded-full bg-[#1e293b] border border-gray-600 z-10' />
        </>
      )}
    </motion.div>
  );
};

export default WorkflowVisualization;
